/**
 * src/components/Financial/PlanetReturnSection.jsx
 * 【完全修正版：リターンチャート視認性強化・目盛り外側配置・ダークモード対応】
 */

import React, { useState, useEffect, useMemo } from "react";
import * as Astro from "../../astronomy.js";
import { styles as commonStyles } from "../../utils/styles.js";
import {
  PLANET_SYMBOLS,
  DEFAULT_PLANET_COLORS,
} from "../../utils/constants.js";
import { Zap, ChevronRight, ChevronLeft, Calendar } from "lucide-react";

// =============================================================================
// 1. 定数定義
// =============================================================================

const MAJOR_PLANETS = [
  "Sun",
  "Moon",
  "Mercury",
  "Venus",
  "Mars",
  "Jupiter",
  "Saturn",
  "Uranus",
  "Neptune",
  "Pluto",
];

const ORB_BY_PLANET = {
  Sun: 0.5,
  Moon: 0.5,
  Mercury: 1.0,
  Venus: 1.0,
  Mars: 2.0,
  Jupiter: 2.0,
  Saturn: 2.0,
  Uranus: 2.0,
  Neptune: 2.0,
  Pluto: 2.0,
};

const PLANET_DAILY_MOTION = {
  Sun: 0.9856,
  Moon: 13.176,
  Mercury: 1.38,
  Venus: 1.2,
  Mars: 0.524,
  Jupiter: 0.083,
  Saturn: 0.033,
  Uranus: 0.011,
  Neptune: 0.006,
  Pluto: 0.004,
};

/**
 * 座標計算用ヘルパー
 */
const getSafePos = function (lon, r, center, angleOffset) {
  if (angleOffset === undefined) angleOffset = 180;
  if (typeof lon !== "number" || isNaN(lon)) return null;
  const rad = (lon + angleOffset) * (Math.PI / 180);
  return {
    x: center + r * Math.cos(rad),
    y: center + r * Math.sin(rad),
  };
};

// =============================================================================
// 2. 内部コンポーネント: ReturnBiWheel (視認性強化・外側目盛り版)
// =============================================================================

const ReturnBiWheel = function (props) {
  const natalPositions = props.natalPositions;
  const currentPositions = props.currentPositions;
  const isMobile = props.isMobile;
  const planetColors = props.planetColors;
  const theme = props.theme;
  const isDark = props.isDark;

  // 凡例と一致させる色定義
  const transitColor = isDark ? "#60a5fa" : "#2c2d96"; // Blue
  const natalColor = "#9c2393"; // Purple

  const size = isMobile === true ? 340 : 540; // ラベルを外に出すためサイズを微増
  const center = size / 2;
  const radiusOuter = size * 0.38; // トランジット円周
  const radiusInner = size * 0.26; // ネイタル円周
  const labelRadius = radiusOuter + 28; // 角度ラベルの配置半径（円の外側）

  const validPlanets = MAJOR_PLANETS.filter(function (p) {
    return (
      natalPositions !== null &&
      natalPositions[p] !== undefined &&
      PLANET_SYMBOLS[p] !== undefined
    );
  });

  const ticks = [];
  for (let i = 0; i < 360; i += 5) {
    ticks.push(i);
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={"0 0 " + size + " " + size}
        style={{ maxWidth: "100%", height: "auto" }}
      >
        {/* トランジット円周 - Blue Solid */}
        <circle
          cx={center}
          cy={center}
          r={radiusOuter}
          fill="none"
          stroke={transitColor}
          strokeWidth="1"
          opacity="0.9"
        />

        {/* ネイタル円周 - Purple Dashed */}
        <circle
          cx={center}
          cy={center}
          r={radiusInner}
          fill="none"
          stroke={natalColor}
          strokeWidth="1"
          strokeDasharray="2,2"
          opacity="0.8"
        />

        {/* 目盛り (Ticks) - トランジット円の外側に配置 */}
        {ticks.map(function (deg) {
          const isMajor = deg % 30 === 0;
          const p1 = getSafePos(deg, radiusOuter, center);
          const p2 = getSafePos(deg, radiusOuter + (isMajor ? 12 : 6), center);
          return (
            <line
              key={"t-" + deg}
              x1={p1.x}
              y1={p1.y}
              x2={p2.x}
              y2={p2.y}
              stroke={isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.3)"}
              strokeWidth={isMajor ? 1.5 : 0.8}
            />
          );
        })}

        {/* 角度ラベル (0, 30, 60...) - 円の外側に配置 */}
        {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(
          function (deg) {
            const pos = getSafePos(deg, labelRadius, center);
            return (
              <text
                key={"lbl-" + deg}
                x={pos.x}
                y={pos.y}
                fontSize="11"
                fontWeight="bold"
                fill={theme.subText}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {deg}°
              </text>
            );
          },
        )}

        {/* 回帰ライン (天体同士の接続) */}
        {validPlanets.map(function (p) {
          const nLon = natalPositions[p];
          const tLon =
            currentPositions !== null ? currentPositions[p] : undefined;
          if (typeof nLon !== "number" || typeof tLon !== "number") return null;

          const diff = Math.abs(((tLon - nLon + 180 + 360) % 360) - 180);
          const isReturning = diff <= (ORB_BY_PLANET[p] || 1.0);

          if (diff <= 15.0) {
            const pNatal = getSafePos(nLon, radiusInner, center);
            const pTransit = getSafePos(tLon, radiusOuter, center);
            if (pNatal === null || pTransit === null) return null;

            return (
              <line
                key={"line-" + p}
                x1={pNatal.x}
                y1={pNatal.y}
                x2={pTransit.x}
                y2={pTransit.y}
                stroke={isReturning === true ? "#ef4444" : theme.border}
                strokeWidth={isReturning === true ? 3 : 1.5}
                strokeDasharray={isReturning === true ? "0" : "4,2"}
                opacity={isReturning === true ? 1 : 0.4}
              />
            );
          }
          return null;
        })}

        {/* Natal天体 (内側) */}
        {validPlanets.map(function (p) {
          const pos = getSafePos(natalPositions[p], radiusInner, center);
          if (pos === null) return null;
          return (
            <g key={"nat-" + p}>
              <circle
                cx={pos.x}
                cy={pos.y}
                r={isMobile === true ? 10 : 12}
                fill={theme.cardBg}
                stroke={natalColor}
                strokeWidth="1.5"
                strokeDasharray="2,1"
              />
              <text
                x={pos.x}
                y={pos.y + 0.5}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={isMobile === true ? 10 : 12}
                fill={natalColor}
                fontWeight="bold"
              >
                {PLANET_SYMBOLS[p]}
              </text>
            </g>
          );
        })}

        {/* Transit天体 (外側) */}
        {validPlanets.map(function (p) {
          const tLon =
            currentPositions !== null ? currentPositions[p] : undefined;
          if (typeof tLon !== "number") return null;
          const pos = getSafePos(tLon, radiusOuter, center);
          if (pos === null) return null;

          const diff = Math.abs(
            ((tLon - natalPositions[p] + 180 + 360) % 360) - 180,
          );
          const isReturning = diff <= (ORB_BY_PLANET[p] || 1.0);
          const color = planetColors[p] || (isDark ? "#fff" : "#2c2d96");

          return (
            <g key={"trans-" + p}>
              {isReturning === true && (
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={isMobile === true ? 16 : 20}
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="2"
                  strokeDasharray="3,2"
                >
                  <animate
                    attributeName="stroke-opacity"
                    values="1;0.2;1"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="r"
                    values={isMobile === true ? "16;20;16" : "20;25;20"}
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
              <circle
                cx={pos.x}
                cy={pos.y}
                r={isMobile === true ? 10 : 12}
                fill={theme.cardBg}
                stroke={isReturning === true ? "#ef4444" : color}
                strokeWidth={2}
              />
              <text
                x={pos.x}
                y={pos.y + 0.5}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={isMobile === true ? 10 : 12}
                fill={isReturning === true ? "#ef4444" : color}
                fontWeight="bold"
              >
                {PLANET_SYMBOLS[p]}
              </text>
            </g>
          );
        })}

        <circle
          cx={center}
          cy={center}
          r={isMobile === true ? 30 : 40}
          fill={theme.subBg}
          stroke={theme.border}
          strokeWidth="1"
        />
        <text
          x={center}
          y={center + 5}
          textAnchor="middle"
          fontSize={isMobile === true ? 9 : 11}
          fill={theme.subText}
          fontWeight="900"
          letterSpacing="1px"
        >
          RETURNS
        </text>
      </svg>

      {/* 凡例テキスト (Transit / Natal) */}
      <div
        style={{
          marginTop: "15px",
          display: "flex",
          gap: "20px",
          fontSize: "11px",
          fontWeight: "bold",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            color: transitColor,
          }}
        >
          <div
            style={{ width: "12px", height: "2px", background: transitColor }}
          />
          Transit (Blue Solid)
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            color: natalColor,
          }}
        >
          <div
            style={{
              width: "12px",
              height: "2px",
              borderBottom: "2px dashed " + natalColor,
            }}
          />
          Natal (Purple Dashed)
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// 3. メインコンポーネント: PlanetReturnSection
// =============================================================================

export default function PlanetReturnSection(props) {
  const natalDate = props.natalDate;
  const natalPositions = props.natalPositions;
  const currentPositions = props.currentPositions;
  const transitDate = props.transitDate;
  const isMobileProp = props.isMobile;
  const planetColors = props.planetColors || DEFAULT_PLANET_COLORS;

  // ダークモード判定
  const isDark = (function () {
    const p = props.isDarkMode;
    if (p === true || p === "true" || p === "dark") return true;
    if (typeof document !== "undefined") {
      var el = document.documentElement;
      var bd = document.body;
      if (
        el.classList.contains("dark") ||
        bd.classList.contains("dark") ||
        el.getAttribute("data-theme") === "dark"
      )
        return true;
    }
    return false;
  })();

  const theme = {
    cardBg: isDark ? "#1c1f26" : "#ffffff",
    subBg: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
    border: isDark ? "#2d313d" : "#e2e8f0",
    text: isDark ? "#ffffff" : "#1e293b",
    subText: isDark ? "#9ca3af" : "#64748b",
  };

  const [returnList, setReturnList] = useState([]);
  const [yearlyReturns, setYearlyReturns] = useState([]);
  const [targetYear, setTargetYear] = useState(new Date().getFullYear());
  const [calculating, setCalculating] = useState(false);
  const [targets, setTargets] = useState({
    Sun: true,
    Moon: true,
    Mercury: true,
    Venus: true,
    Mars: true,
    Jupiter: true,
    Saturn: true,
    Uranus: true,
    Neptune: true,
    Pluto: true,
  });

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );

  useEffect(function () {
    const handleResize = function () {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const effectiveIsMobile = useMemo(
    function () {
      if (typeof isMobileProp === "boolean") return isMobileProp;
      return windowWidth < 1024;
    },
    [isMobileProp, windowWidth],
  );

  const effectiveTransitDate = useMemo(
    function () {
      if (
        transitDate !== null &&
        transitDate !== undefined &&
        transitDate instanceof Date &&
        !isNaN(transitDate.getTime())
      ) {
        return transitDate;
      }
      return new Date();
    },
    [transitDate],
  );

  /**
   * 単発の「次のリターン」計算ロジック
   */
  useEffect(
    function () {
      if (
        natalDate === null ||
        natalDate === undefined ||
        natalPositions === null ||
        currentPositions === null
      ) {
        setReturnList([]);
        return;
      }

      const timer = setTimeout(function () {
        try {
          const planets = MAJOR_PLANETS.filter(function (p) {
            return targets[p] === true;
          });
          const results = planets
            .map(function (p) {
              const nLon = natalPositions[p];
              const cLon = currentPositions[p];
              if (typeof nLon !== "number" || typeof cLon !== "number")
                return null;

              const diff = Math.abs(((cLon - nLon + 180 + 360) % 360) - 180);
              const orbThreshold = ORB_BY_PLANET[p] || 1.0;
              const isReturning = diff <= orbThreshold;

              let nextDate = null;
              try {
                if (typeof Astro.calculateNextPlanetReturn === "function") {
                  nextDate = Astro.calculateNextPlanetReturn(
                    p,
                    effectiveTransitDate,
                    nLon,
                  );
                }
              } catch (e) {}

              let daysLeft = 0;
              if (nextDate !== null && !isNaN(nextDate.getTime())) {
                daysLeft = Math.ceil(
                  (nextDate.getTime() - effectiveTransitDate.getTime()) /
                    86400000,
                );
              } else {
                let distance = (nLon - cLon + 360) % 360;
                if (distance < 0.1 && isReturning === false) distance += 360;
                daysLeft = Math.ceil(distance / (PLANET_DAILY_MOTION[p] || 1));
                nextDate = new Date(
                  effectiveTransitDate.getTime() + daysLeft * 86400000,
                );
              }
              return {
                planet: p,
                date: nextDate,
                daysLeft: daysLeft,
                isReturning: isReturning,
                orb: diff,
              };
            })
            .filter(function (x) {
              return x !== null;
            });
          setReturnList(
            results.sort(function (a, b) {
              return a.daysLeft - b.daysLeft;
            }),
          );
        } catch (err) {
          console.error(err);
        }
      }, 100);
      return function () {
        clearTimeout(timer);
      };
    },
    [
      natalDate,
      natalPositions,
      currentPositions,
      effectiveTransitDate,
      targets,
    ],
  );

  /**
   * 指定年の全回帰日一覧を計算するロジック
   */
  useEffect(
    function () {
      if (natalPositions === null) return;
      setCalculating(true);

      const calcYearly = setTimeout(function () {
        try {
          const results = [];
          const startOfYear = new Date(targetYear, 0, 1, 0, 0, 0);
          const endOfYear = new Date(targetYear, 11, 31, 23, 59, 59);
          const planets = MAJOR_PLANETS.filter(function (p) {
            return targets[p] === true;
          });

          planets.forEach(function (p) {
            const nLon = natalPositions[p];
            if (typeof nLon !== "number") return;

            let searchStart = new Date(startOfYear);
            while (searchStart < endOfYear) {
              let foundDate = null;
              try {
                if (typeof Astro.calculateNextPlanetReturn === "function") {
                  foundDate = Astro.calculateNextPlanetReturn(
                    p,
                    searchStart,
                    nLon,
                  );
                }
              } catch (e) {
                break;
              }

              if (
                foundDate === null ||
                isNaN(foundDate.getTime()) ||
                foundDate > endOfYear
              ) {
                break;
              }

              results.push({
                planet: p,
                date: foundDate,
                year: targetYear,
              });

              const offset = p === "Moon" ? 20 : 1;
              searchStart = new Date(foundDate.getTime() + offset * 86400000);
            }
          });

          setYearlyReturns(
            results.sort(function (a, b) {
              return a.date.getTime() - b.date.getTime();
            }),
          );
        } catch (err) {
          console.error(err);
        } finally {
          setCalculating(false);
        }
      }, 200);

      return function () {
        clearTimeout(calcYearly);
      };
    },
    [targetYear, natalPositions, targets],
  );

  const activeReturns = useMemo(
    function () {
      return returnList.filter(function (item) {
        return item.isReturning === true;
      });
    },
    [returnList],
  );

  const toggleTarget = function (p) {
    setTargets(function (prev) {
      const next = Object.assign({}, prev);
      next[p] = !prev[p];
      return next;
    });
  };

  const handleYearChange = function (offset) {
    setTargetYear(function (prev) {
      return prev + offset;
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "25px",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* 惑星選択コントロール */}
      <div
        style={{
          padding: "20px",
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
        }}
      >
        <div
          style={{
            fontSize: "11px",
            fontWeight: "900",
            color: theme.subText,
            marginBottom: "15px",
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}
        >
          分析対象天体の選択
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          {MAJOR_PLANETS.map(function (p) {
            const color = planetColors[p] || (isDark ? "#fff" : "#333");
            const isActive = targets[p] === true;
            return (
              <button
                key={p}
                onClick={function () {
                  toggleTarget(p);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  borderRadius: "10px",
                  border: "1px solid",
                  borderColor: isActive ? color : theme.border,
                  background: isActive
                    ? isDark
                      ? "rgba(255,255,255,0.05)"
                      : color + "10"
                    : theme.subBg,
                  color: isActive ? color : theme.subText,
                  fontSize: "13px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                <span style={{ fontSize: "16px" }}>{PLANET_SYMBOLS[p]}</span>
                {p}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            effectiveIsMobile === true ? "1fr" : "repeat(12, 1fr)",
          gap: "25px",
          width: "100%",
        }}
      >
        {/* チャートブロック (span 7) */}
        <div
          style={{
            gridColumn: effectiveIsMobile === true ? "span 1" : "span 7",
            background: theme.cardBg,
            borderRadius: "15px",
            border: "1px solid " + theme.border,
            padding: "30px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <ReturnBiWheel
            natalPositions={natalPositions}
            currentPositions={currentPositions}
            isMobile={effectiveIsMobile}
            planetColors={planetColors}
            theme={theme}
            isDark={isDark}
          />
        </div>

        {/* リストエリア (span 5) */}
        <div
          style={{
            gridColumn: effectiveIsMobile === true ? "span 1" : "span 5",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          {/* 現在リターン中の惑星サマリーブロック */}
          {activeReturns.length > 0 && (
            <div
              style={{
                padding: "24px",
                background: isDark ? "rgba(234, 179, 8, 0.05)" : "#fffcf0",
                borderRadius: "15px",
                border:
                  "1px solid " +
                  (isDark ? "rgba(234, 179, 8, 0.2)" : "#f9d97f"),
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "20px",
                }}
              >
                <Zap size={18} color="#eab308" />
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "900",
                    color: "#eab308",
                    letterSpacing: "0.1em",
                  }}
                >
                  RETURNS ACTIVE
                </span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                {activeReturns.map(function (item) {
                  return (
                    <div
                      key={item.planet}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "12px 18px",
                        background: theme.cardBg,
                        borderRadius: "12px",
                        border: "1px solid " + theme.border,
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "24px",
                          color: planetColors[item.planet] || theme.text,
                        }}
                      >
                        {PLANET_SYMBOLS[item.planet]}
                      </span>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: theme.text,
                          }}
                        >
                          {item.planet}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: "900",
                            color: "#eab308",
                          }}
                        >
                          ORB: {item.orb.toFixed(2)}°
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 年次回帰スケジュールセクション (カード型デザイン) */}
          <div
            style={{
              background: theme.cardBg,
              borderRadius: "15px",
              border: "1px solid " + theme.border,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* --- 修正ポイント：モバイル時にタイトルと操作系を2段にする --- */}
            <div
              style={{
                padding: effectiveIsMobile ? "20px 24px" : "18px 24px",
                borderBottom: "1px solid " + theme.border,
                display: "flex",
                flexDirection: effectiveIsMobile ? "column" : "row", // ★2段にする
                alignItems: effectiveIsMobile ? "flex-start" : "center",
                justifyContent: "space-between",
                gap: effectiveIsMobile ? "15px" : "0", // ★段間の余白を追加
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Calendar size={18} color="rgb(147, 39, 143)" />
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "15px",
                    color: theme.text,
                  }}
                >
                  {targetYear}年 回帰一覧
                </span>
              </div>

              {/* ボタン群を右寄せ（モバイル時は左寄せ/全幅） */}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  width: effectiveIsMobile ? "100%" : "auto",
                  justifyContent: effectiveIsMobile ? "flex-end" : "flex-end",
                }}
              >
                <button
                  onClick={function () {
                    handleYearChange(-1);
                  }}
                  style={{
                    background: theme.subBg,
                    border: "1px solid " + theme.border,
                    borderRadius: "6px",
                    padding: "6px 12px", // モバイルで押しやすいよう少し大きく
                    color: theme.text,
                    cursor: "pointer",
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={function () {
                    setTargetYear(new Date().getFullYear());
                  }}
                  style={{
                    background: theme.subBg,
                    border: "1px solid " + theme.border,
                    borderRadius: "6px",
                    padding: "6px 16px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: theme.text,
                    cursor: "pointer",
                  }}
                >
                  今年
                </button>
                <button
                  onClick={function () {
                    handleYearChange(1);
                  }}
                  style={{
                    background: theme.subBg,
                    border: "1px solid " + theme.border,
                    borderRadius: "6px",
                    padding: "6px 12px",
                    color: theme.text,
                    cursor: "pointer",
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div
              style={{
                maxHeight: "600px",
                overflowY: "auto",
                padding: "15px",
              }}
            >
              {calculating === true ? (
                <div
                  style={{
                    padding: "60px",
                    textAlign: "center",
                    color: theme.subText,
                    fontSize: "13px",
                  }}
                >
                  回帰データを精査中...
                </div>
              ) : yearlyReturns.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {yearlyReturns.map(function (item, idx) {
                    const color =
                      planetColors[item.planet] ||
                      (isDark ? "#fff" : "#2c2d96");
                    const isPast = item.date < new Date();

                    return (
                      <div
                        key={item.planet + "-" + idx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "14px 18px",
                          borderRadius: "12px",
                          background: theme.subBg,
                          border:
                            "1px solid " +
                            (isPast ? "transparent" : theme.border),
                          position: "relative",
                          overflow: "hidden",
                          opacity: isPast ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: "4px",
                            backgroundColor: color,
                          }}
                        />

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "15px",
                          }}
                        >
                          <span style={{ fontSize: "22px", color: color }}>
                            {PLANET_SYMBOLS[item.planet]}
                          </span>
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: theme.text,
                              }}
                            >
                              {item.planet}
                            </span>
                            <span
                              style={{
                                fontSize: "9px",
                                fontWeight: "800",
                                color: theme.subText,
                                letterSpacing: "0.5px",
                              }}
                            >
                              {isPast ? "PAST RETURN" : "FUTURE RETURN"}
                            </span>
                          </div>
                        </div>

                        <div style={{ textAlign: "right" }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: "900",
                              color: isPast ? theme.subText : theme.text,
                            }}
                          >
                            {item.date.toLocaleDateString("ja-JP", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })}
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: theme.subText,
                              fontWeight: "bold",
                            }}
                          >
                            {item.date.toLocaleTimeString("ja-JP", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  style={{
                    padding: "60px 20px",
                    textAlign: "center",
                    color: theme.subText,
                    fontSize: "12px",
                  }}
                >
                  この年の回帰データはありません。
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
